import { useQuery } from "@apollo/client";
import { ReactElement } from "react";
import { useParams } from "react-router-dom";

import {
  GetRegistrationForStaffQuery,
  GetRegistrationForStaffQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import ErrorCard from "~frontend/components/ErrorCard";
import PageContainer from "~frontend/components/PageContainer";
import { GET_REGISTRATION_FOR_STAFF } from "~frontend/graphql/tenDlcRegistrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";

import { Forms } from "./Forms";
import { TenDlcProvider } from "./TenDlcProvider";
import { UUID_REGEX, TCR_BRAND_ID_REGEX } from "./idRegexes";

export const TenDlcRegistration = (): ReactElement => {
  const { brandRegistrationId } = useParams<{
    brandRegistrationId: string;
    campaignRegistrationId?: string;
  }>();
  useBreadcrumbs([
    {
      label: "Compliance Registrations",
      path: `/staff/compliance-registrations/`,
    },
  ]);

  let getRegistrationInput:
    | GetRegistrationForStaffQueryVariables["input"]
    | null = null;
  if (brandRegistrationId && UUID_REGEX.test(brandRegistrationId)) {
    getRegistrationInput = { brandRegistrationId };
  } else if (
    brandRegistrationId &&
    TCR_BRAND_ID_REGEX.test(brandRegistrationId)
  ) {
    getRegistrationInput = { tcrBrandId: brandRegistrationId };
  }

  const {
    data: brandData,
    loading,
    error,
  } = useQuery<
    GetRegistrationForStaffQuery,
    GetRegistrationForStaffQueryVariables
  >(GET_REGISTRATION_FOR_STAFF, {
    variables: {
      input: getRegistrationInput ?? {},
    },

    skip: !getRegistrationInput,
  });

  if (!getRegistrationInput) {
    return (
      <PageContainer maxWidth="xl">
        <ErrorCard
          errors={[
            {
              message: `Brand registration ID "${brandRegistrationId}" is not a valid UUID or TCR ID`,
            },
          ]}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="xl">
      <AsyncWrapper loading={loading} errors={error} data={brandData}>
        {({ getRegistrationForStaff: brand }) => (
          <TenDlcProvider brand={brand}>
            <Forms />
          </TenDlcProvider>
        )}
      </AsyncWrapper>
    </PageContainer>
  );
};
